import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  Tabs,
  Tab,
  Pagination,
} from "@mui/material";
import { format } from "date-fns";
import { useApi } from "../../hooks/useApi";
import { useTenant } from "../../hooks/useTenant";
import useIsMobile from "../../shared_modules/useIsMobile";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CalendarCard from "./calendarCard";
import AvailableTimes from "./availableTimes";
import { formatTime } from "../../utils/functions";

const STATUS_COLORS = {
  confirmed: "info",
  completed: "success",
  cancelled: "error",
  no_show: "warning",
  pending: "default",
};

const PAYMENT_STATUS_COLORS = {
  paid: "success",
  unpaid: "warning",
  pending: "warning",
  refunded: "info",
};

const PAYMENT_STATUS_LABELS = {
  paid: "Paid",
  unpaid: "Pay at appointment",
  pending: "Payment pending",
  refunded: "Refunded",
};

const PAGE_SIZE = 10;

const ClientAppointments = () => {
  const api = useApi();
  const { tenant } = useTenant();
  const isMobile = useIsMobile();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [pastPage, setPastPage] = useState(1);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(null);

  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(new Date());
  const [rescheduleSlot, setRescheduleSlot] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleError, setRescheduleError] = useState(null);
  const [rescheduleSuccess, setRescheduleSuccess] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api("/bookings/bookings.php", { method: "GET" });
        setBookings(res.data.bookings || []);
      } catch (e) {
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    setCancelError(null);

    try {
      const res = await api("/stripe/cancel-booking.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ booking_id: cancelTarget.id }),
      });
      setCancelSuccess(res.data.message);
      setBookings((prev) =>
        prev.map((b) =>
          b.id === cancelTarget.id
            ? {
                ...b,
                status: "cancelled",
                payment_status:
                  cancelTarget.payment_method === "card" &&
                  !isPastDeadline(cancelTarget)
                    ? "refunded"
                    : res.data.fee_charged
                      ? "paid"
                      : b.payment_status,
              }
            : b,
        ),
      );
      setCancelTarget(null);
    } catch (e) {
      setCancelError(e.message || "Failed to cancel booking.");
    } finally {
      setCancelling(false);
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleSlot || !rescheduleTarget) return;
    setRescheduling(true);
    setRescheduleError(null);
    try {
      await api("/bookings/reschedule.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          booking_id: rescheduleTarget.id,
          starts_at: rescheduleSlot.start_utc,
        }),
      });
      const res = await api("/bookings/bookings.php", { method: "GET" });
      setBookings(res.data.bookings || []);
      setRescheduleTarget(null);
      setRescheduleSlot(null);
      setRescheduleSuccess(
        "Your appointment has been rescheduled successfully.",
      );
    } catch (e) {
      setRescheduleError(e.message || "Failed to reschedule booking.");
    } finally {
      setRescheduling(false);
    }
  };

  // ── Time helpers ──────────────────────────────────────────────────────────
  // COMPARISONS use the TRUE UTC instant: `starts_at` is the canonical UTC value,
  // parsed with an explicit "Z" so it's correct regardless of the viewer's
  // browser timezone. DISPLAY helpers use `starts_at_local` (the shop wall-clock)
  // so the shown time always matches the shop, never the viewer's location.
  const startInstantMs = (b) =>
    b?.starts_at ? new Date(b.starts_at.replace(" ", "T") + "Z").getTime() : 0;

  const isPastDeadline = (b) => {
    const windowHours = tenant?.cancellation_window_hours ?? 24;
    return Date.now() >= startInstantMs(b) - windowHours * 3600000;
  };

  const getCancelDeadline = (b) => {
    const windowHours = tenant?.cancellation_window_hours ?? 24;
    const local = b.starts_at_local || b.starts_at;
    const deadline = new Date(local.replace(" ", "T"));
    deadline.setHours(deadline.getHours() - windowHours);
    return format(deadline, "EEE, MMM d 'at' h:mm a");
  };

  const calcFee = (price) => {
    const p = Number(price);
    const feeRate = tenant?.processing_fee_rate ?? 0.025;
    const subtotal = p + Math.round(p * feeRate * 100) / 100;
    return (Math.round(((subtotal + 0.3) / 0.971) * 100) / 100).toFixed(2);
  };

  const isFuture = (b) => startInstantMs(b) > Date.now();

  const isCardRefunded = (b) =>
    b.status === "cancelled" &&
    b.payment_method === "card" &&
    b.payment_status === "refunded";
  const isCardForfeited = (b) =>
    b.status === "cancelled" &&
    b.payment_method === "card" &&
    b.payment_status === "paid";
  const isCashCleanCancel = (b) =>
    b.status === "cancelled" &&
    b.payment_method === "cash" &&
    b.payment_status === "unpaid";
  const isCashLateCancelFee = (b) =>
    b.status === "cancelled" &&
    b.payment_method === "cash" &&
    b.payment_status === "paid";
  const isCardNoShow = (b) =>
    b.status === "no_show" &&
    b.payment_method === "card" &&
    b.payment_status === "paid";
  const isCashNoShowFee = (b) =>
    b.status === "no_show" &&
    b.payment_method === "cash" &&
    b.payment_status === "paid";

  const getPaymentMethodLabel = (b) => {
    if (isCashLateCancelFee(b) || isCashNoShowFee(b)) return "💳 Card";
    if (b.payment_method === "card") return "💳 Card";
    return "💵 Cash";
  };

  const getPaymentStatusLabel = (b) => {
    if (isCardRefunded(b)) return "Refunded";
    if (isCardForfeited(b)) return "Non-refundable";
    if (isCashCleanCancel(b)) return "Cancelled";
    if (isCashLateCancelFee(b)) return "Late cancellation fee";
    if (isCardNoShow(b)) return "Paid at booking";
    if (isCashNoShowFee(b)) return "No-show fee charged";
    return PAYMENT_STATUS_LABELS[b.payment_status] || b.payment_status;
  };

  const getPaymentStatusColor = (b) => {
    if (isCardRefunded(b)) return "info";
    if (isCardForfeited(b)) return "error";
    if (isCashCleanCancel(b)) return "default";
    if (isCashLateCancelFee(b)) return "error";
    if (isCardNoShow(b)) return "default";
    if (isCashNoShowFee(b)) return "error";
    return PAYMENT_STATUS_COLORS[b.payment_status] || "default";
  };

  const getAmountDisplay = (b) => {
    if (isCardRefunded(b)) return `$${Number(b.price_charged).toFixed(2)}`;
    if (isCardForfeited(b) && b.amount_paid)
      return `$${Number(b.amount_paid).toFixed(2)}`;
    if (isCashCleanCancel(b)) return `$${Number(b.price_charged).toFixed(2)}`;
    if (isCashLateCancelFee(b) && b.amount_paid)
      return `$${Number(b.amount_paid).toFixed(2)} charged`;
    if (isCardNoShow(b) && b.amount_paid)
      return `$${Number(b.amount_paid).toFixed(2)}`;
    if (isCashNoShowFee(b) && b.amount_paid)
      return `$${Number(b.amount_paid).toFixed(2)} charged`;
    return `$${Number(b.price_charged).toFixed(2)}`;
  };

  // Split into upcoming (confirmed + future) and past (everything else)
  const upcoming = bookings
    .filter((b) => b.status === "confirmed" && isFuture(b))
    .sort((a, b) => startInstantMs(a) - startInstantMs(b));

  const past = bookings
    .filter((b) => !(b.status === "confirmed" && isFuture(b)))
    .sort((a, b) => startInstantMs(b) - startInstantMs(a));

  const totalPastPages = Math.ceil(past.length / PAGE_SIZE);
  const pagedPast = past.slice(
    (pastPage - 1) * PAGE_SIZE,
    pastPage * PAGE_SIZE,
  );

  const activeList = tab === 0 ? upcoming : pagedPast;

  if (loading) {
    return (
      <Box sx={{ p: 5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <CircularProgress size={18} sx={{ color: "text.secondary" }} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Loading appointments...
        </Typography>
      </Box>
    );
  }

  const renderCard = (b) => {
    const startsAt = b.starts_at_local || b.starts_at; // shop wall-clock — display only
    const canCancel = b.status === "confirmed" && isFuture(b);
    const canReschedule = canCancel && !isPastDeadline(b);
    const pastDeadline = isPastDeadline(b);

    return (
      <Box
        key={b.id}
        component={Paper}
        elevation={1}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {b.service?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              with {b.barber?.first_name} {b.barber?.last_name}
            </Typography>
          </Box>
          <Chip
            label={b.status.replace("_", " ").toUpperCase()}
            color={STATUS_COLORS[b.status] || "default"}
            size="small"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: "text.third", fontWeight: 600 }}
          >
            {format(new Date(startsAt), "EEE, MMM d")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {format(new Date(startsAt), "h:mm a")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {getAmountDisplay(b)}
          </Typography>
          {b.payment_method === "card" && Number(b.tip_amount) > 0 && (
            <Typography
              variant="caption"
              sx={{ color: "#C4984D", fontSize: "0.7rem", fontWeight: 600 }}
            >
              +${Number(b.tip_amount).toFixed(2)} tip
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            mb: b.notes || canCancel ? 1.5 : 0,
          }}
        >
          <Chip
            label={getPaymentMethodLabel(b)}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.7rem", height: 20, borderRadius: 1 }}
          />
          <Chip
            label={getPaymentStatusLabel(b)}
            color={getPaymentStatusColor(b)}
            size="small"
            sx={{ fontSize: "0.7rem", height: 20 }}
          />
        </Box>

        {b.notes && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              mb: canCancel ? 1.5 : 0,
            }}
          >
            <StickyNote2OutlinedIcon
              sx={{
                fontSize: 13,
                color: "text.secondary",
                mt: "2px",
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", lineHeight: 1.5 }}
            >
              {b.notes}
            </Typography>
          </Box>
        )}

        {canCancel && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              pt: 1.5,
              borderTop: "0.1px solid",
              borderColor: "customDivider",
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {pastDeadline ? (
                b.payment_method === "card" ? (
                  "⚠️ Past free cancellation window — your payment is non-refundable"
                ) : (
                  `⚠️ Past free cancellation window — a $${calcFee(b.price_charged)} fee applies`
                )
              ) : (
                <>
                  Free cancellation until
                  <br />
                  {getCancelDeadline(b)}
                </>
              )}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              {canReschedule && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EventAvailableIcon />}
                  onClick={() => {
                    setRescheduleTarget(b);
                    // Open the picker on the booking's existing day (shop-local),
                    // not today, so its time slots show immediately.
                    const [datePart] = (b.starts_at_local || b.starts_at || "")
                      .split(" ");
                    const [yy, mm, dd] = datePart.split("-").map(Number);
                    setRescheduleDate(yy ? new Date(yy, mm - 1, dd) : new Date());
                    setRescheduleSlot(null);
                    setRescheduleError(null);
                  }}
                  sx={{ fontSize: "0.75rem" }}
                >
                  Reschedule
                </Button>
              )}
              <Button
                size="small"
                color={pastDeadline ? "error" : "primary"}
                variant="outlined"
                onClick={() => setCancelTarget(b)}
                sx={{ fontSize: "0.75rem" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 3, md: 5 }, maxWidth: "900px" }}>
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        Appointments
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Manage your upcoming and past appointments.
      </Typography>

      {cancelSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setCancelSuccess(null)}
        >
          {cancelSuccess}
        </Alert>
      )}
      {rescheduleSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setRescheduleSuccess(null)}
        >
          {rescheduleSuccess}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => {
          setTab(v);
          setPastPage(1);
        }}
        sx={{ mb: 3, borderBottom: "1px solid", borderColor: "customDivider" }}
      >
        <Tab
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Upcoming
              {upcoming.length > 0 && (
                <Box
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.08)",
                    color: "text.secondary",
                    borderRadius: "10px",
                    px: 0.8,
                    py: 0.1,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    lineHeight: 1.6,
                  }}
                >
                  {upcoming.length}
                </Box>
              )}
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Past
              {past.length > 0 && (
                <Box
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.08)",
                    color: "text.secondary",
                    borderRadius: "10px",
                    px: 0.8,
                    py: 0.1,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    lineHeight: 1.6,
                  }}
                >
                  {past.length}
                </Box>
              )}
            </Box>
          }
        />
      </Tabs>

      {/* Empty states */}
      {activeList.length === 0 ? (
        <Box
          component={Paper}
          elevation={1}
          sx={{
            p: 3,
            borderRadius: 2,
            border: "0.1px solid",
            borderColor: "customDivider",
            backgroundColor: "offWhite",
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {tab === 0 ? "No upcoming appointments." : "No past appointments."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {activeList.map(renderCard)}
        </Box>
      )}

      {/* Pagination — Past tab only */}
      {tab === 1 && totalPastPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPastPages}
            page={pastPage}
            onChange={(_, p) => {
              setPastPage(p);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            color="primary"
            size={isMobile ? "small" : "medium"}
          />
        </Box>
      )}

      {/* Reschedule dialog */}
      <Dialog
        open={!!rescheduleTarget}
        onClose={() => !rescheduling && setRescheduleTarget(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Reschedule Appointment
        </DialogTitle>
        <DialogContent sx={{ "&&": { pt: 2 } }}>
          {rescheduleTarget && (
            <>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 3 }}
              >
                Select a new date and time with{" "}
                <strong>
                  {rescheduleTarget.barber?.first_name}{" "}
                  {rescheduleTarget.barber?.last_name}
                </strong>
                .
              </Typography>
              <Grid container spacing={2}>
                <Grid size={isMobile ? 12 : 6}>
                  <CalendarCard
                    selected={rescheduleDate}
                    setSelected={(d) => {
                      setRescheduleDate(d);
                      setRescheduleSlot(null);
                    }}
                  />
                </Grid>
                <Grid size={isMobile ? 12 : 6}>
                  <AvailableTimes
                    barber={{
                      user_id: rescheduleTarget.barber_user_id,
                      first_name: rescheduleTarget.barber?.first_name,
                      last_name: rescheduleTarget.barber?.last_name,
                    }}
                    service={{
                      service_id: rescheduleTarget.service_id,
                      name: rescheduleTarget.service?.name,
                      duration_minutes:
                        rescheduleTarget.service?.duration_minutes,
                    }}
                    selected={rescheduleDate}
                    selectedSlot={rescheduleSlot}
                    setSelectedSlot={setRescheduleSlot}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {rescheduleError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {rescheduleError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setRescheduleTarget(null)}
            disabled={rescheduling}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReschedule}
            disabled={!rescheduleSlot || rescheduling}
          >
            {rescheduling ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : rescheduleSlot ? (
              `Confirm — ${formatTime(rescheduleSlot.start)}`
            ) : (
              "Select a time"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog
        open={!!cancelTarget}
        onClose={() => !cancelling && setCancelTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          {cancelTarget && isPastDeadline(cancelTarget) ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                You're past the free cancellation window.
              </Alert>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {cancelTarget.payment_method === "card" ? (
                  "Your payment is non-refundable. The barber has already been paid."
                ) : (
                  <>
                    A cancellation fee of{" "}
                    <strong>${calcFee(cancelTarget.price_charged)}</strong> will
                    be charged to your card on file.
                  </>
                )}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Are you sure you want to cancel this appointment?
              {cancelTarget?.payment_method === "card" && (
                <span>
                  {" "}
                  <strong>
                    $
                    {(
                      Number(cancelTarget.price_charged) +
                      Number(cancelTarget.tip_amount || 0)
                    ).toFixed(2)}
                  </strong>
                  {Number(cancelTarget.tip_amount) > 0
                    ? " (service + tip)"
                    : ""}{" "}
                  will be refunded within 5–10 business days. The processing fee
                  is non-refundable.
                </span>
              )}
            </Typography>
          )}
          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelling}>
            Keep Appointment
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? <CircularProgress size={16} /> : "Yes, Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAppointments;
